import { Autocomplete, Box, TextField } from '@mui/material';
import appStyles from 'app/providers/theme/App.module.css';
import { useEffect, useRef, useState } from 'react';
import { colors } from 'app/providers/theme/colors-type';
import UgskLogo from 'shared/assets/UgskLogo';
import styles from './FeedbackMobApp.module.css';
import { useFormRedux } from 'shared/utils/hooks/useFormRedux';
import { updateField } from 'entities/feedback-mob-app';
import { validationSchema } from 'entities/feedback-mob-app/utils/validationSchema';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import CustomButton from 'shared/custom-button/CustomButton';
import { fetchFeedbackMobApp } from 'entities/feedback-mob-app/action/fetch-feedback-mob-app';
import Loader from 'shared/loader/Loader';
import { errorCodeFeedbackMobApp, errorFeedbackMobApp, isLoadingFeedbackMobApp } from 'entities/feedback-mob-app/selector';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import * as yup from 'yup';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const FeedbackMobApp: React.FC = () => {
    const [errors, setErrors] = useState<Record<string, string>>({});

    const {
        formState,
        handleChange,
        handleBlur,
        handleDateChange,
        formatPhone,
        validateForm,
    } = useFormRedux(
        (state) => state.feedbackMobApp,
        updateField,
        validationSchema
    );
    const emailDomains = [
        'gmail.com',
        'yandex.ru',
        'mail.ru',
        'rambler.ru',
        'yahoo.com',
        'outlook.com',
        'icloud.com',
        'bk.ru',
        'inbox.ru',
        'list.ru',
    ];

    const [emailInput, setEmailInput] = useState(formState.Email);
    const [expandedPanel, setExpandedPanel] = useState<number | false>(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        setEmailInput(formState.Email);
    }, [formState.Email]);

    const handleEmailInputChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        setEmailInput(newValue);
        dispatch(updateField({ field: 'Email', value: newValue }));
    };

    const handleDomainSelect = (event: React.ChangeEvent<{}>, newValue: string | null) => {
        if (newValue) {
            const [localPart] = emailInput.split('@');
            const newEmail = `${localPart}@${newValue}`;
            setEmailInput(newEmail);
            dispatch(updateField({ field: 'Email', value: newEmail }));
        }
    };

    const filterDomains = (options: string[], state: { inputValue: string }) => {
        const inputValue = state.inputValue.trim();
        if (!inputValue.includes('@')) {
            return [];
        }
        const domainPart = inputValue.split('@')[1] || '';
        return options.filter(option =>
            option.toLowerCase().includes(domainPart.toLowerCase())
        );
    };
    const textFieldRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (textFieldRef.current) {
            textFieldRef.current.style.height = 'auto';
            textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`;
        }
    }, [formState.Description]);

    const validateFeedback = async () => {
        try {
            const schema = validationSchema;

            await schema.validate(formState, { abortEarly: false });
            setErrors({});
            return true;
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                const newErrors: Record<string, string> = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }
                });
                setErrors(newErrors);
                return false;
            }
            return false;
        }
    };

    const handleSend = async () => {
        const isValid = await validateFeedback();
        if (isValid) {
            dispatch(fetchFeedbackMobApp({
                Name: formState.Name,
                Phone: formState.Phone,
                Email: formState.Email,
                BirthDate: formState.BirthDate,
                Description: formState.Description,
            }));
        }

    }

    const isLoading = useAppSelector(isLoadingFeedbackMobApp);
    const errorCode = useAppSelector(errorCodeFeedbackMobApp);
    const errorMessage = useAppSelector(errorFeedbackMobApp);

    if (isLoading) {
        return <Loader title="Отправка запроса..." />;
    }
    if (errorCode === 0) {
        return (
            <Box className={styles.mainBox}>
                <UgskLogo />
                <Box className={styles.container}>
                    <Box className={styles.successMessage}>
                        <span><CheckCircleOutlineIcon /></span>
                        <p>Заявка на удаление учетных данных отправлена!</p>
                    </Box>
                    <Box className={styles.colorBox}>
                        <span className={styles.icon}><InfoIcon /></span>
                        <p>Обработка заявок занимает до 30 рабочих дней</p>
                    </Box>
                </Box>

            </Box>

        )
    }
    if (errorCode === 1) {
        return (
            <Box className={styles.mainBox}>
                <UgskLogo />
                <Box className={styles.container}>
                    <Box className={styles.errorMessage}>
                        <span><ErrorOutlineIcon /></span>
                        <p>{errorMessage}</p>
                    </Box>
                    <Box className={styles.btnBox}>
                        <CustomButton
                            title="Отправить еще раз"
                            handleClick={handleSend}
                            textColor="white"
                            backgroundColor={colors.primary}
                            width="200px"
                            gap="10px"
                            borderRadius='4px'
                            padding='3px'
                        />
                    </Box>
                </Box>

            </Box>

        )
    }

    return (
        <Box className={styles.mainBox}>
            <UgskLogo />
            <Box className={styles.container}>
                <Box className={appStyles.titleBox}>
                    <h1>Удаление учетной записи в мобильном приложении</h1>
                </Box>
                <form id="feedback-form">
                    <TextField
                        name="Name"
                        label="ФИО"
                        value={formState.Name}
                        onChange={handleChange('Name')}
                        error={!!errors.Name}
                        helperText={errors.Name}
                        className={styles.Fio}
                    />
                    <Box className={styles.secondLine}>
                        <TextField
                            name="BirthDate"
                            label="Дата рождения"
                            variant="outlined"
                            value={formState.BirthDate}
                            onChange={handleDateChange('BirthDate')}
                            placeholder="дд.мм.гггг"
                            onBlur={handleBlur('BirthDate')}
                            error={!!errors.BirthDate}
                            helperText={errors.BirthDate}
                        />
                        <TextField
                            name="Phone"
                            label="Телефон"
                            variant="outlined"
                            className={styles.fullWidth}
                            value={formatPhone(formState.Phone)}
                            onChange={handleChange('Phone')}
                            onBlur={handleBlur('Phone')}
                            error={!!errors.Phone}
                            helperText={errors.Phone}
                        />
                        <Autocomplete
                            freeSolo
                            options={emailDomains}
                            value={null}
                            inputValue={emailInput}
                            onInputChange={handleEmailInputChange}
                            onChange={handleDomainSelect}
                            filterOptions={filterDomains}
                            getOptionLabel={(option) => option}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="Email"
                                    label="Почта"
                                    variant="outlined"
                                    className={styles.fullWidth}
                                    error={!!errors.Email}
                                    helperText={errors.Email}
                                />
                            )}
                        />
                    </Box>

                    <TextField
                        inputRef={textFieldRef}
                        label="Оставьте комментарий"
                        multiline
                        rows={1}
                        value={formState.Description}
                        name="Description"
                        onChange={handleChange('Description')}
                        variant="outlined"
                        error={!!errors.Description}
                        helperText={errors.Description || ''}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                height: 'auto',
                                alignItems: 'flex-start',
                                minHeight: '80px'
                            },
                        }}
                    />
                    <Box className={styles.btnBox}>
                        <CustomButton
                            title="Отправить заявку"
                            handleClick={handleSend}
                            textColor="white"
                            backgroundColor={colors.primary}
                            width="200px"
                            gap="10px"
                            borderRadius='4px'
                            padding='3px'
                        />
                    </Box>

                </form>
            </Box>
        </Box >
    );
};

export default FeedbackMobApp;
